import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BiDownload, BiCheckCircle } from 'react-icons/bi';
import { usePostHog } from 'posthog-js/react';
import { useApproveOrRejectEstimateMutation } from '../../app/services/orders';
import { toggleModal } from '../../features/ui/uiSlice';
import { useGetEstimatePaymentUrlQuery } from '../../app/services/orders';
import Modal from '../../components/Modal/Modal';

function ModalContent({ estimate, order }) {
    const priorAuthNeeded = order?.priorAuthStatus === 'PRIOR_AUTH_NEEDED';
    if (priorAuthNeeded) {
        //Prior Auth Needed
        return (
            <>
                <p className="text-lg mb-2">
                    Your insurance requires a{' '}
                    <span className="font-bold">Prior Authorization (PA)</span> for your CPAP. We’ll
                    take care of getting that approved first. Once approved, a{' '}
                    <span className="font-bold">GEM Patient Care Specialist</span> will contact you
                    by phone to collect payment. We must receive your initial payment before your
                    CPAP can ship.
                </p>
                <p className="font-bold">GEM offers flexible payment options:</p>
                <ul className="list-disc pl-6 my-4">
                    <li>Zero Interest Payment Plans</li>
                    <li>Use your HSA/FSA funds</li>
                    <li>Major credit/debit cards accepted</li>
                </ul>
            </>
        );
        //Amount to pay
    } else if (estimate?.totalAmount > 0) {
        return (
            <>
                <p className="mb-2">
                    Based on your current insurance information, there is no initial payment
                    required for your CPAP. We’re now working on setting up your CPAP. You’ll be
                    notified by email as soon as it ships.
                </p>
                <p className="font-bold">GEM offers a variety of flexible payment options:</p>

                <ul className="list-disc pl-6 my-4">
                    <li>Zero Interest Payment Plans</li>
                    <li>Use your HSA/FSA funds</li>
                    <li>Major credit/debit cards accepted</li>
                </ul>
                <p className="font-bold mt-6">
                    A GEM Patient Care Specialist will reach out via phone call to get you set-up.
                </p>
            </>
        );
        //Nothing to pay
    } else {
        return (
            <p>
                Based on your current insurance information, there is no initial payment required
                for your CPAP. We’re now working on setting up your CPAP. You’ll be notified by
                email as soon as it ships.
            </p>
        );
    }
}

// Leave out for now.
{
    /* <a href={paymentUrl.url} target="_blank" rel="noreferrer">
    <button
        className="btn-primary text-base lg:text-lg w-full mt-4 flex items-center justify-center gap-4"
        onClick={() => {
            posthog?.capture('Estimate Payment Clicked', {
                estimateId: estimateId,
            });
        }}>
        <span>Pay Now</span>
        <FaExternalLinkAlt />
    </button>
</a>; */
}

function Estimate() {
    const posthog = usePostHog();
    const dispatch = useDispatch();
    const { estimateId } = useParams();
    const navigate = useNavigate();
    const { data: paymentUrl } = useGetEstimatePaymentUrlQuery(estimateId);

    console.log('payment_url:', paymentUrl);

    const [approveOrRejectEstimate] = useApproveOrRejectEstimateMutation();

    //Redux state
    const { orderHistory } = useSelector((state) => state.orders);
    const { storageToken } = useSelector((state) => state.auth);

    //Local state
    const [submitting, setSubmitting] = useState(false);
    const [approvedEstimate, setApprovedEstimate] = useState(false);

    const order = orderHistory?.find((order) => order.estimate?.id.toString() === estimateId);
    const { estimate } = order || null;

    const estimateFileId = order?.estimate?.files.find((file) => file.fileType === 'ESTIMATE').id;

    const baseDownloadUrl = process.env.REACT_APP_API_BASE_URL
        ? process.env.REACT_APP_API_BASE_URL
        : '';

    return (
        <div className="max-w-xl space-y-4">
            <div>
                <h2 className="font-heading text-4xl sm:text-[44px]">GEM SLEEP CPAP ESTIMATE</h2>

                {estimate?.totalAmount > 0 ? (
                    <div className="py-4">
                        <p>
                            Please review the attached estimate for your CPAP machine and 90-day
                            supply package, based on your current insurance coverage. This estimate
                            is valid for 30 days. To proceed with shipping your CPAP, we must
                            receive both your approval and initial payment.
                        </p>
                    </div>
                ) : (
                    <p>
                        We worked with your insurance to create an estimate for your CPAP machine
                        and 90 days of supplies. This estimate is valid for 30 days, we <b>must</b>{' '}
                        receive approval to ship your device.
                    </p>
                )}
            </div>
            {estimateFileId && (
                <div className="pb-8">
                    <a
                        className="btn-shell text-base lg:text-lg w-full flex items-center justify-center gap-4"
                        href={`${baseDownloadUrl}/files/${estimateFileId}?storageToken=${storageToken}`}
                        download
                        target="_blank"
                        rel="noreferrer">
                        <span>View My Estimate</span>
                        <BiDownload className="opacity-50" />
                    </a>
                </div>
            )}

            <button
                className="btn-primary text-base lg:text-lg w-full flex items-center justify-center gap-4"
                disabled={submitting}
                onClick={async () => {
                    setSubmitting(true);
                    const approval = await approveOrRejectEstimate({
                        estimateId,
                        fileId: estimateFileId,
                        status: 'APPROVED',
                    }).unwrap();

                    if (approval) {
                        setSubmitting(false);
                        setApprovedEstimate(true);

                        posthog?.capture('Estimate Approved', { estimateId: estimateId });
                    } else {
                        toast.error('Something went wrong, please try again.');
                        setSubmitting(false);
                    }
                }}>
                <span>I APPROVE THE ESTIMATE</span>
                <BiCheckCircle className="opacity-50" />
            </button>
            <button
                disabled={submitting}
                className="btn-shell text-base lg:text-lg w-full"
                onClick={async () => {
                    setSubmitting(true);
                    const approval = await approveOrRejectEstimate({
                        estimateId,
                        fileId: estimateFileId,
                        status: 'REJECTED',
                    }).unwrap();

                    if (approval) {
                        setSubmitting(false);
                        navigate('/dashboard');
                        posthog?.capture('Estimate Rejected', { estimateId: estimateId });
                    } else {
                        toast.error('Something went wrong, please try again.');
                        setSubmitting(false);
                    }
                }}>
                NOT AT THIS TIME
            </button>

            <p className="font-bold">Important Notes:</p>
            <ol className="lg:list-disc">
                {['PRIOR_AUTH_NEEDED', 'PRIOR_AUTH_AFTER_CONSULT'].includes(
                    order?.priorAuthStatus,
                ) && (
                    <li key="prior-auth-required">
                        Your insurance company requires we obtain a Prior Authorization (PA) before
                        your CPAP can ship, this process can take up to 15 business days. The PA
                        will be submitted once we have approval on the estimate.{' '}
                    </li>
                )}
                <li key="approval-notice">
                    By approving the estimate, you are agreeing to pay any patient balance due.
                    Payment plans are available.
                </li>
            </ol>

            <p>
                If you have questions regarding your estimate, please{' '}
                <button onClick={() => dispatch(toggleModal('contact'))} className="link">
                    contact us
                </button>
            </p>
            <Modal
                showClose={true}
                isOpen={approvedEstimate}
                width="lg:w-[500px]"
                closeHandler={() => {
                    setApprovedEstimate(false);
                    navigate('/dashboard');
                }}>
                <h3 className="mb-3 font-heading text-3xl">We've Got Your Estimate Approval!</h3>

                <ModalContent estimate={estimate} order={order} />
            </Modal>
        </div>
    );
}

export default Estimate;
